<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import PageState from './PageState.vue';
import type { PlanDependency, PlanTask } from '@/types/domain';
import { formatResponsibility } from '@/utils/format';
import { actualStyle, barStyle, createGanttRange, monthTicks, pixelsToDays, shiftDay, todayPosition } from '@/utils/gantt';
import { buildProgressIndex, progressTitle, type NodeProgress } from '@/utils/plan-progress';
import { confirmAction } from '@/services/confirm';

type ZoomLevel = 'compact' | 'standard' | 'detail';
type TaskScope = 'phases' | 'key' | 'all' | 'unscheduled';

const props = withDefaults(defineProps<{
  tasks: PlanTask[];
  asOf: string;
  rangeTasks?: PlanTask[];
  canWrite?: boolean;
  savingTaskId?: string | number | null;
  focusTaskId?: string | number | null;
  /** 从 Gate 点进来时希望默认展开到哪一档；不传沿用「阶段概览」 */
  preferredScope?: TaskScope | null;
  dependencies?: PlanDependency[];
}>(), {
  canWrite: true,
  savingTaskId: null,
  focusTaskId: null,
  preferredScope: null,
  dependencies: () => [],
});
const emit = defineEmits<{
  /** 点击行 → 打开右侧抽屉编辑；甘特行只有 54px，塞不下日期+状态+历史 */
  open: [task: PlanTask];
  /** 拖拽甘特条直接改期：手势本身就是确认，不再弹一次框 */
  drag: [task: PlanTask, payload: { start: string; end: string }];
  /** 在图上连线建前置：前置完成后后置才能开始 */
  link: [payload: { predecessorId: number; successorId: number }];
  unlink: [dependency: PlanDependency];
}>();

const STATUS_LABEL: Readonly<Record<string, string>> = {
  pending: '待处理',
  in_progress: '进行中',
  blocked: '受阻',
  completed: '已完成',
  approved: '已批准',
  rejected: '已退回',
  not_applicable: '不适用',
};
const zoom = ref<ZoomLevel>('standard');
const taskScope = ref<TaskScope>('phases');
const scrollPort = ref<HTMLElement | null>(null);
const dragging = ref(false);
const dragPointerId = ref<number | null>(null);
const dragStartX = ref(0);
const dragStartScrollLeft = ref(0);

const zoomOptions: Array<{ value: ZoomLevel; label: string; width: number }> = [
  { value: 'compact', label: '紧凑', width: 1180 },
  { value: 'standard', label: '标准', width: 1680 },
  { value: 'detail', label: '详细', width: 2360 },
];

const canvasWidth = computed(() => zoomOptions.find((option) => option.value === zoom.value)?.width ?? 1680);
const phaseTasks = computed(() => props.tasks.filter((task) =>
  task.parentId === null || /phase|stage|阶段/i.test(task.phaseCode)));
const keyTasks = computed(() => props.tasks.filter((task) =>
  task.keyDeliverable
  || task.parentId === null
  || /phase|stage|阶段/i.test(task.phaseCode)));
const unscheduled = computed(() => props.tasks.filter((task) => !task.planStart || !task.planEnd));
const scopeOptions = computed<Array<{ value: TaskScope; label: string; count: number }>>(() => [
  { value: 'phases', label: '阶段概览', count: phaseTasks.value.length },
  { value: 'key', label: '关键节点', count: keyTasks.value.length },
  { value: 'all', label: '全部任务', count: props.tasks.length },
  { value: 'unscheduled', label: '待排期', count: unscheduled.value.length },
]);
/**
 * 阶段概览做成可展开的树。
 *
 * 原来点阶段行直接弹编辑抽屉，看不到这个阶段下面有哪些活动（试用反馈 0818）。
 * 现在点阶段名 = 展开/收起子节点，编辑挪到行尾那个「改期 / 状态」独立按钮上——
 * 一个手势一种含义，不再让整行既是导航又是编辑入口。
 */
const expandedPhases = ref<Set<string>>(new Set());

function childrenOf(task: PlanTask): PlanTask[] {
  const key = String(task.activityId ?? task.id);
  return props.tasks.filter((item) => item.parentId !== null && String(item.parentId) === key);
}
function hasChildren(task: PlanTask): boolean {
  return childrenOf(task).length > 0;
}
function isExpanded(task: PlanTask): boolean {
  return expandedPhases.value.has(String(task.activityId ?? task.id));
}
function toggleExpand(task: PlanTask): void {
  const key = String(task.activityId ?? task.id);
  const next = new Set(expandedPhases.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  expandedPhases.value = next;
}

const displayedTasks = computed(() => {
  if (taskScope.value === 'all') return props.tasks;
  if (taskScope.value === 'unscheduled') return unscheduled.value;
  if (taskScope.value === 'key') return keyTasks.value;
  // 阶段概览：阶段行 + 已展开阶段的直属子节点，按原顺序插在父行后面
  const rows: PlanTask[] = [];
  for (const phase of phaseTasks.value) {
    rows.push(phase);
    if (isExpanded(phase)) rows.push(...childrenOf(phase));
  }
  return rows;
});
const scheduled = computed(() => displayedTasks.value.filter((task) => task.planStart && task.planEnd));
const scheduledTotal = computed(() => props.tasks.filter((task) => task.planStart && task.planEnd).length);
const range = computed(() => createGanttRange(props.rangeTasks ?? props.tasks));
const ticks = computed(() => range.value ? monthTicks(range.value) : []);
const today = computed(() => range.value ? todayPosition(range.value, new Date(props.asOf)) : null);

function planBarStyle(task: PlanTask) {
  return range.value ? barStyle(task, range.value) : null;
}

function completedBarStyle(task: PlanTask) {
  return range.value ? actualStyle(task, range.value) : null;
}

/*
  完成度（0930 Tony：「同步到计划甘特，甘特图上标注对应的完成度」）。

  数和「阶段工作」那张表同一个算法（utils/plan-progress）：做完算 100、阶段按下面的末端活动平均。
  按**整个项目**的节点算（rangeTasks），不按当前筛出来的几行——
  开着「只看我负责的」时阶段条要是只按我那几条算，同一个阶段换个筛选就换个数。
  条子本身画成「浅底 + 深色填充」：填充的长度就是完成度，没人报过的就只有浅底。
*/
const progressIndex = computed(() => buildProgressIndex(props.rangeTasks ?? props.tasks));
const NO_PROGRESS: NodeProgress = { value: null, source: 'none' };

function progressOf(task: PlanTask): NodeProgress {
  return progressIndex.value.get(String(task.id)) ?? NO_PROGRESS;
}

/** 条内填充的宽度（%）；没有数就是 0 */
function fillWidth(task: PlanTask): number {
  return progressOf(task).value ?? 0;
}

/** 条上的字：有数才写；做完的写「完成」比写 100% 好认 */
function barLabel(task: PlanTask): string {
  const progress = progressOf(task);
  if (progress.value === null) return '';
  return progress.source === 'done' ? '完成' : `${progress.value}%`;
}

/** 行名下面那行小字里的完成度 */
function progressNote(task: PlanTask): string {
  const progress = progressOf(task);
  if (progress.value === null || progress.source === 'done') return '';
  return progress.source === 'rollup' ? `汇总 ${progress.value}%` : `完成 ${progress.value}%`;
}

/** 条子的悬停说明：日期、状态、完成度怎么来的 */
function barTitle(task: PlanTask): string {
  const progress = progressOf(task);
  const parts = [`${rangeLabel(task)}（${statusLabel(task.status)}）`];
  if (progress.value !== null) parts.push(`完成度 ${progress.value}%：${progressTitle(progress)}`);
  if (props.canWrite !== false) parts.push('可拖动改期');
  return parts.join(' · ');
}

function ownerLabel(task: PlanTask): string {
  return formatResponsibility(task.ownerName || task.ownerRole);
}

function missingDateLabel(task: PlanTask): string {
  if (!task.planStart && !task.planEnd) return '缺开始与完成日期';
  return task.planStart ? '缺计划完成日期' : '缺计划开始日期';
}

function needsDates(task: PlanTask): boolean {
  return !task.planStart || !task.planEnd;
}

/** 已排期的行叫「改期」，缺日期的行叫「补计划」——同一个入口，说人话 */
function rowActionLabel(task: PlanTask): string {
  if (props.canWrite === false) return '只读';
  return needsDates(task) ? '补计划' : '改期 / 状态';
}

function statusLabel(status: string | null | undefined): string {
  if (!status) return '待处理';
  return STATUS_LABEL[status] ?? status;
}
/** 把 `2025-11-01 00:00:00+08` 这种原始时间戳收拾成 `2025-11-01` */
function plainDate(value: string | null | undefined): string {
  return value ? String(value).slice(0, 10) : '—';
}

function rangeLabel(task: PlanTask): string {
  return `${plainDate(task.planStart)} → ${plainDate(task.planEnd)}`;
}

/**
 * 打开编辑抽屉。
 *
 * 在甘特行里就地展开表单试过不行：一行只有 54px 且外层 overflow:hidden，
 * 日期 + 状态 + 变更历史铺不开。编辑放右侧抽屉，甘特只负责定位和拖拽。
 *
 * 入口是行尾的「改期 / 状态」按钮——点行名是展开子节点（试用反馈 0818）。
 */
function toggleSchedule(task: PlanTask): void {
  if (props.canWrite === false) return;
  emit('open', task);
}

/** 点行名：有子节点就展开，叶子节点仍然直接打开编辑 */
function onRowNameClick(task: PlanTask): void {
  if (onRowLinkClick(task)) return;
  if (taskScope.value === 'phases' && hasChildren(task)) {
    toggleExpand(task);
    return;
  }
  toggleSchedule(task);
}

/**
 * 拖拽甘特条改期。
 *
 * 三种手势：拖中段整体平移（工期不变）、拖左端改开始、拖右端改完成。
 * 直接操作的手势本身就是确认，落下即写入——再弹一次确认框反而比点开表单还慢。
 * 写入失败会由上层回滚并提示。
 */
const dragState = ref<{
  task: PlanTask;
  mode: 'move' | 'start' | 'end';
  originX: number;
  trackWidth: number;
  baseStart: string;
  baseEnd: string;
} | null>(null);
const dragPreview = ref<{
  id: string | number;
  start: string;
  end: string;
  /** 相对原计划的位移天数，正为后移 */
  days: number;
  name: string;
  mode: 'move' | 'start' | 'end';
} | null>(null);

/**
 * 拖动时跟着指针走的实时标签。
 *
 * 原本只更新甘特条的 title，但原生 tooltip 在拖拽过程中不会刷新——
 * 等于人在盲拖，松手才知道落到哪天。用 position: fixed 渲染，
 * 这样不会被 .timeline-cell 的 overflow:hidden 裁掉。
 */
const dragPointer = ref<{ x: number; y: number } | null>(null);

function beginDrag(event: PointerEvent, task: PlanTask, mode: 'move' | 'start' | 'end'): void {
  if (props.canWrite === false) return;
  if (!task.planStart || !task.planEnd || !range.value) return;
  const track = (event.currentTarget as HTMLElement).closest('.timeline-cell');
  if (!track) return;
  event.stopPropagation();
  event.preventDefault();
  (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  dragPointer.value = { x: event.clientX, y: event.clientY };
  dragState.value = {
    task,
    mode,
    originX: event.clientX,
    trackWidth: track.clientWidth,
    baseStart: task.planStart.slice(0, 10),
    baseEnd: task.planEnd.slice(0, 10),
  };
  dragPreview.value = {
    id: task.id,
    start: dragState.value.baseStart,
    end: dragState.value.baseEnd,
    days: 0,
    name: task.name,
    mode,
  };
}

function moveDrag(event: PointerEvent): void {
  const state = dragState.value;
  if (!state || !range.value) return;
  const days = pixelsToDays(event.clientX - state.originX, state.trackWidth, range.value);
  let start = state.baseStart;
  let end = state.baseEnd;
  if (state.mode === 'move') {
    start = shiftDay(state.baseStart, days);
    end = shiftDay(state.baseEnd, days);
  } else if (state.mode === 'start') {
    start = shiftDay(state.baseStart, days);
    if (start > end) start = end;   // 不允许把开始拖过完成
  } else {
    end = shiftDay(state.baseEnd, days);
    if (end < start) end = start;
  }
  dragPointer.value = { x: event.clientX, y: event.clientY };
  dragPreview.value = { id: state.task.id, start, end, days, name: state.task.name, mode: state.mode };
}

function endDrag(): void {
  const state = dragState.value;
  const preview = dragPreview.value;
  dragState.value = null;
  dragPreview.value = null;
  dragPointer.value = null;
  if (!state || !preview) return;
  // 没挪动就当没发生，避免一次误触产生一条审计
  if (preview.start === state.baseStart && preview.end === state.baseEnd) return;
  emit('drag', state.task, { start: preview.start, end: preview.end });
}

/** 拖动过程中用预览日期画条，松手前就能看到落点 */
function previewedTask(task: PlanTask): PlanTask {
  const preview = dragPreview.value;
  if (!preview || String(preview.id) !== String(task.id)) return task;
  return { ...task, planStart: preview.start, planEnd: preview.end };
}

function isSaving(task: PlanTask): boolean {
  return props.savingTaskId !== null
    && props.savingTaskId !== undefined
    && String(props.savingTaskId) === String(task.id);
}

async function focusRequestedTask(): Promise<void> {
  if (props.focusTaskId === null || props.focusTaskId === undefined) return;
  const task = props.tasks.find((item) => String(item.id) === String(props.focusTaskId));
  if (!task) return;
  taskScope.value = task.planStart && task.planEnd ? 'all' : 'unscheduled';
  await nextTick();
  document.querySelector(`[data-gantt-node-id="${String(task.id)}"]`)
    ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
}

// 从 Gate 进来只显示一个阶段头，而面板上写着「阶段任务 73」，自相矛盾
watch(
  () => props.preferredScope,
  (next) => { if (next) taskScope.value = next; },
  { immediate: true },
);

watch(
  () => [props.focusTaskId, props.tasks] as const,
  () => { void focusRequestedTask(); },
  { immediate: true },
);

/* ---------- 前后置依赖 ---------- */

const showDependencies = ref(true);
const linkFrom = ref<PlanTask | null>(null);
const boardEl = ref<HTMLElement | null>(null);
const connectors = ref<Array<{
  dependency: PlanDependency;
  d: string;
  arrow: string;
}>>([]);

const linking = computed(() => linkFrom.value !== null);

const dependencyCount = computed(() => {
  const map = new Map<string, { pred: number; succ: number }>();
  for (const item of props.dependencies) {
    const succ = map.get(String(item.successorId)) ?? { pred: 0, succ: 0 };
    succ.pred += 1;
    map.set(String(item.successorId), succ);
    const pred = map.get(String(item.predecessorId)) ?? { pred: 0, succ: 0 };
    pred.succ += 1;
    map.set(String(item.predecessorId), pred);
  }
  return map;
});

function dependencyBadge(task: PlanTask): { pred: number; succ: number } | null {
  return dependencyCount.value.get(String(task.id)) ?? null;
}

/** 只看「本行作为后置」的冲突：前置那一端不该被标红，它自己没排错 */
function violatedFor(task: PlanTask): boolean {
  return props.dependencies.some((item) =>
    item.violated && String(item.successorId) === String(task.id));
}

function dependencyTitle(task: PlanTask): string {
  const badge = dependencyBadge(task);
  if (!badge) return '';
  const parts = [`前置 ${badge.pred} 条 · 后置 ${badge.succ} 条`];
  for (const item of props.dependencies) {
    if (item.violated && String(item.successorId) === String(task.id)) {
      parts.push(`违反：需等“${item.predecessorName}”，最早可开始 ${item.earliestStart ?? '—'}`);
    }
  }
  return parts.join('\n');
}

/** 当前行范围里画不出来的依赖：两端至少有一端没显示或没排期 */
const hiddenDependencies = computed(() =>
  props.dependencies.length - connectors.value.length);

/**
 * 连线几何。
 *
 * 位置从 DOM 量，不按 CSS 常量推算：行高会随内容换行变化，甘特条是百分比定位，
 * 缩放档位还会改画布宽度——任何一处对不上，线就会画到别的行上去，
 * 而画歪了的依赖线比不画更容易让人误判先后关系。
 */
function measureConnectors(): void {
  const board = boardEl.value;
  if (!board || !showDependencies.value) {
    connectors.value = [];
    return;
  }
  const origin = board.getBoundingClientRect();
  const barOf = (id: number | string): DOMRect | null => {
    const bar = board.querySelector(`[data-gantt-node-id="${String(id)}"] .plan-bar`);
    return bar ? bar.getBoundingClientRect() : null;
  };
  const next: typeof connectors.value = [];
  for (const dependency of props.dependencies) {
    const from = barOf(dependency.predecessorId);
    const to = barOf(dependency.successorId);
    if (!from || !to) continue;
    const x1 = from.right - origin.left;
    const y1 = from.top + from.height / 2 - origin.top;
    const x2 = to.left - origin.left;
    const y2 = to.top + to.height / 2 - origin.top;
    // 折线绕出去再拐回来，免得后置在前置左边时线直接横穿过整条前置条
    const out = x2 > x1 + 24 ? (x1 + x2) / 2 : x1 + 16;
    const back = x2 - 10;
    const d = out <= back
      ? `M ${x1} ${y1} H ${out} V ${y2} H ${back}`
      : `M ${x1} ${y1} H ${out} V ${(y1 + y2) / 2} H ${back - 18} V ${y2} H ${back}`;
    next.push({
      dependency,
      d,
      arrow: `M ${x2} ${y2} l -7 -4 l 0 8 z`,
    });
  }
  connectors.value = next;
}

function scheduleMeasure(): void {
  void nextTick(() => {
    measureConnectors();
    // 布局稳定后再量一次：字体/滚动条会让首帧的位置略有偏差
    window.requestAnimationFrame(measureConnectors);
  });
}

function onRowLinkClick(task: PlanTask): boolean {
  if (!linking.value) return false;
  const from = linkFrom.value;
  if (!from) return false;
  if (String(from.id) === String(task.id)) {
    linkFrom.value = null;
    return true;
  }
  emit('link', { predecessorId: Number(from.id), successorId: Number(task.id) });
  linkFrom.value = null;
  return true;
}

function startLinking(task: PlanTask): void {
  linkFrom.value = task;
}

function cancelLinking(): void {
  linkFrom.value = null;
}

async function onConnectorClick(dependency: PlanDependency): Promise<void> {
  if (props.canWrite === false) return;
  if (!await confirmAction({ title: `确认解除依赖：“${dependency.predecessorName}” → “${dependency.successorName}”？`, })) return;
  emit('unlink', dependency);
}

watch(
  () => [props.dependencies, props.tasks, zoom.value, taskScope.value, showDependencies.value] as const,
  scheduleMeasure,
  { immediate: true, deep: true },
);

function onLinkKey(event: KeyboardEvent): void {
  if (event.key === 'Escape' && linking.value) cancelLinking();
}

onMounted(() => {
  window.addEventListener('resize', measureConnectors);
  window.addEventListener('keydown', onLinkKey);
  scheduleMeasure();
});

onUnmounted(() => {
  window.removeEventListener('resize', measureConnectors);
  window.removeEventListener('keydown', onLinkKey);
});

function onWheel(event: WheelEvent) {
  const target = scrollPort.value;
  if (!target) return;
  const maxScrollLeft = Math.max(0, target.scrollWidth - target.clientWidth);
  if (!maxScrollLeft) return;
  const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
  if (!delta) return;
  const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, target.scrollLeft + delta));
  if (nextScrollLeft === target.scrollLeft) return;
  target.scrollLeft = nextScrollLeft;
  event.preventDefault();
}

function onPointerDown(event: PointerEvent) {
  if (event.button !== 0) return;
  const eventTarget = event.target as HTMLElement | null;
  if (eventTarget?.closest('button, input, select, textarea, a')) return;
  const target = scrollPort.value;
  if (!target) return;
  dragging.value = true;
  dragPointerId.value = event.pointerId;
  dragStartX.value = event.clientX;
  dragStartScrollLeft.value = target.scrollLeft;
  target.setPointerCapture?.(event.pointerId);
}

function onPointerMove(event: PointerEvent) {
  const target = scrollPort.value;
  if (!target || !dragging.value || event.pointerId !== dragPointerId.value) return;
  const nextScrollLeft = dragStartScrollLeft.value - (event.clientX - dragStartX.value);
  if (nextScrollLeft !== target.scrollLeft) {
    target.scrollLeft = nextScrollLeft;
    event.preventDefault();
  }
}

function stopDragging(event: PointerEvent) {
  const target = scrollPort.value;
  if (!dragging.value || event.pointerId !== dragPointerId.value) return;
  if (target?.hasPointerCapture?.(event.pointerId)) target.releasePointerCapture(event.pointerId);
  dragging.value = false;
  dragPointerId.value = null;
}
</script>

<template>
  <section class="gantt-wrap">
    <div v-if="tasks.length" class="gantt-toolbar">
      <div v-if="range" class="zoom-control" role="group" aria-label="甘特图缩放">
        <span>时间轴密度</span>
        <button
          v-for="option in zoomOptions"
          :key="option.value"
          type="button"
          :class="{ active: zoom === option.value }"
          :aria-pressed="zoom === option.value"
          @click="zoom = option.value"
        >
          {{ option.label }}
        </button>
      </div>
      <div class="task-scope-control" role="group" aria-label="甘特图行范围">
        <span>行范围</span>
        <button
          v-for="option in scopeOptions"
          :key="option.value"
          type="button"
          :class="{ active: taskScope === option.value }"
          :aria-pressed="taskScope === option.value"
          @click="taskScope = option.value"
        >
          {{ option.label }} <b>{{ option.count }}</b>
        </button>
      </div>
      <div class="dep-control" role="group" aria-label="前后置依赖">
        <span>依赖</span>
        <button
          type="button"
          :class="{ active: showDependencies }"
          :aria-pressed="showDependencies"
          @click="showDependencies = !showDependencies"
        >显示连线 <b>{{ dependencies.length }}</b></button>
        <span v-if="showDependencies && hiddenDependencies > 0" class="dep-hidden">
          {{ hiddenDependencies }} 条两端不在当前行范围或未排期，画不出来
        </span>
      </div>
    </div>

    <p v-if="linking" class="dep-linking-bar">
      正在设前置：<b>{{ linkFrom?.name }}</b> 完成后 →
      点另一行把它设为后置。<button type="button" @click="cancelLinking">取消</button>
    </p>

    <div
      v-if="range || unscheduled.length"
      ref="scrollPort"
      class="gantt-scroll"
      :class="{ dragging }"
      tabindex="0"
      aria-label="甘特图，可横向滚动或拖拽平移"
      @wheel="onWheel"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="stopDragging"
      @pointercancel="stopDragging"
    >
      <div ref="boardEl" class="gantt-board" :class="{ linking }" :style="{ width: `${canvasWidth}px` }">
        <!--
          依赖连线画在整块画布上，坐标是量出来的像素。
          放在甘特条下面、粘住的左树列下面（z-index 2 < 3），
          横向滚动时线会自然被树列挡住，不会糊在任务名上。
        -->
        <svg v-if="showDependencies && connectors.length" class="dep-layer" aria-hidden="true">
          <g
            v-for="item in connectors"
            :key="item.dependency.id"
            :class="['dep-link', { violated: item.dependency.violated, clickable: canWrite !== false }]"
            @click="onConnectorClick(item.dependency)"
          >
            <title>
              {{ item.dependency.predecessorName }} → {{ item.dependency.successorName }}{{
                item.dependency.lagDays ? `（间隔 ${item.dependency.lagDays} 天）` : '' }}{{
                item.dependency.violated ? `｜当前日期违反：最早可开始 ${item.dependency.earliestStart}` : '' }}
            </title>
            <path class="dep-hit" :d="item.d" />
            <path class="dep-line" :d="item.d" />
            <path class="dep-arrow" :d="item.arrow" />
          </g>
        </svg>
        <div class="gantt-head" style="position: relative; top: 0">
          <div class="gantt-tree-head">
            <span>WBS / 任务节点</span>
            <b>{{ scheduled.length }} / {{ displayedTasks.length }} 当前已排期 · 全项目 {{ scheduledTotal }}</b>
          </div>
          <div class="timeline-head">
            <span v-for="tick in ticks" :key="tick.label" :style="{ left: `${tick.position}%` }">{{ tick.label }}</span>
            <span v-if="!range" class="no-range-head">补齐首个排期后生成时间轴</span>
          </div>
        </div>
        <div
          v-for="task in displayedTasks"
          :key="task.id"
          class="gantt-row"
          :class="{
            unscheduled: !task.planStart || !task.planEnd,
            focused: focusTaskId !== null && focusTaskId !== undefined && String(task.id) === String(focusTaskId),
          }"
          :data-gantt-node-id="task.id"
        >
          <!--
            整行可点。原先只有「缺日期」的行有按钮，已排期的 174 行是纯只读的——
            而工程师每天要动的恰恰是已排期那些（改期、标进行中、标完成）。
          -->
          <div class="gantt-tree-cell" :style="{ paddingLeft: task.parentId ? '30px' : '16px' }">
          <component
            :is="canWrite === false ? 'div' : 'button'"
            class="tree-name"
            :type="canWrite === false ? undefined : 'button'"
            :aria-expanded="taskScope === 'phases' && hasChildren(task) ? isExpanded(task) : undefined"
            :aria-label="canWrite === false ? undefined
              : taskScope === 'phases' && hasChildren(task)
                ? `${isExpanded(task) ? '收起' : '展开'}${task.name}下面的活动`
                : `${needsDates(task) ? '补齐' : '调整'}${task.name}的计划日期与状态`"
            @click="onRowNameClick(task)"
          >
            <!--
              有子节点的阶段行给个箭头：点它是展开，不是编辑。
              没有子节点也要留这一格，否则子行和阶段行首对不齐。
            -->
            <span class="tree-caret" aria-hidden="true">{{
              taskScope === 'phases' && hasChildren(task) ? (isExpanded(task) ? '▾' : '▸') : ''
            }}</span>
            <span class="wbs-code">{{ task.phaseCode }}</span>
            <div>
              <b>{{ task.name }}</b>
              <small>
                {{ ownerLabel(task) }} · {{ statusLabel(task.status) }}
                <template v-if="progressNote(task)"> · {{ progressNote(task) }}</template>
                <template v-if="taskScope === 'phases' && hasChildren(task)">
                  · {{ childrenOf(task).length }} 项活动
                </template>
              </small>
            </div>
            <span class="node-markers">
              <i v-if="task.keyDeliverable" title="关键交付物">●</i>
              <i v-if="task.approvalRequired" title="需要审批">A</i>
              <i v-if="task.overdue" class="risk" title="逾期">!</i>
              <i
                v-if="dependencyBadge(task)"
                class="dep-badge"
                :class="{ risk: violatedFor(task) }"
                :title="dependencyTitle(task)"
              >⇥{{ dependencyBadge(task)?.pred }}/{{ dependencyBadge(task)?.succ }}</i>
            </span>
          </component>
          <!--
            编辑入口单独一个按钮：点行名归展开子节点了，一个手势不能两种含义。
            只读账号仍然看得到这一格，但是个不可点的标签——写着「只读」比凭空少一列好懂。
          -->
          <component
            :is="canWrite === false ? 'span' : 'button'"
            class="row-action"
            :class="{ missing: canWrite !== false && needsDates(task) }"
            :type="canWrite === false ? undefined : 'button'"
            :aria-label="canWrite === false ? undefined
              : `${needsDates(task) ? '补齐' : '调整'}${task.name}的计划日期与状态`"
            @click.stop="canWrite === false ? undefined : (onRowLinkClick(task) || toggleSchedule(task))"
          >{{ linking ? '设为后置' : rowActionLabel(task) }}</component>
          </div>
          <!-- 连线入口单独一个按钮：整行点击已经归「改期」了，不能一个手势两种含义 -->
          <button
            v-if="canWrite !== false && !linking && task.planStart && task.planEnd"
            class="dep-start"
            type="button"
            :aria-label="`把 ${task.name} 设为某个任务的前置`"
            title="以本行为前置，连一条依赖"
            @click.stop="startLinking(task)"
          >⇥</button>
          <div class="timeline-cell">
            <i v-for="tick in ticks" :key="tick.label" class="month-line" :style="{ left: `${tick.position}%` }"></i>
            <i v-if="today !== null" class="today-line" :style="{ left: `${today}%` }"><b>今日</b></i>
            <div
              v-if="planBarStyle(previewedTask(task))"
              class="plan-bar"
              :class="{
                overdue: task.overdue && progressOf(task).source !== 'done',
                completed: progressOf(task).source === 'done',
                'has-progress': fillWidth(task) > 0,
                'label-on-fill': fillWidth(task) >= 80,
                dragging: dragPreview !== null && String(dragPreview.id) === String(task.id),
                draggable: canWrite !== false,
                'dep-violated': violatedFor(task),
                'link-source': linkFrom !== null && String(linkFrom.id) === String(task.id),
              }"
              :style="planBarStyle(previewedTask(task)) || undefined"
              :title="dragPreview !== null && String(dragPreview.id) === String(task.id)
                ? `${dragPreview.start} → ${dragPreview.end}`
                : barTitle(task)"
              @pointerdown="beginDrag($event, task, 'move')"
              @pointermove="moveDrag"
              @pointerup="endDrag"
              @pointercancel="endDrag"
            >
              <!-- 完成度：深色填充的长度就是完成度（0930） -->
              <i v-if="fillWidth(task) > 0" class="bar-fill" :style="{ width: `${fillWidth(task)}%` }" aria-hidden="true"></i>
              <i
                v-if="canWrite !== false"
                class="bar-handle start"
                aria-hidden="true"
                @pointerdown="beginDrag($event, task, 'start')"
                @pointermove="moveDrag"
                @pointerup="endDrag"
              ></i>
              <span>{{ barLabel(task) }}</span>
              <i
                v-if="canWrite !== false"
                class="bar-handle end"
                aria-hidden="true"
                @pointerdown="beginDrag($event, task, 'end')"
                @pointermove="moveDrag"
                @pointerup="endDrag"
              ></i>
            </div>
            <i v-if="completedBarStyle(task)" class="actual-bar" :style="completedBarStyle(task) || undefined"></i>
            <div v-if="needsDates(task)" class="inline-schedule">
              <span><b>{{ missingDateLabel(task) }}</b><small>点这一行补齐后立即显示为甘特条</small></span>
            </div>
          </div>
        </div>
        <div v-if="!displayedTasks.length" class="gantt-empty-row">当前筛选下没有计划节点。</div>
        <div class="gantt-legend">
          <span><i class="plan"></i>计划</span><span><i class="progress"></i>完成度（条内深色部分，阶段按下面的活动汇总）</span><span><i class="done"></i>已完成</span><span><i class="actual"></i>实际完成</span><span><i class="today"></i>今日线</span><span><i class="missing"></i>待排期</span>
          <span>● 关键交付物</span><span>A 审批</span><span class="risk-text">! 逾期</span>
        </div>
      </div>
    </div>
    <PageState v-else state="empty" title="暂无计划节点" message="接口没有返回计划节点，系统不会用静态任务替代。" />

    <!-- 拖动时跟着指针的实时日期。position:fixed 才不会被时间轴的 overflow:hidden 裁掉 -->
    <div
      v-if="dragPreview && dragPointer"
      class="drag-tip"
      :style="{ left: `${dragPointer.x + 14}px`, top: `${dragPointer.y - 46}px` }"
      aria-hidden="true"
    >
      <b>{{ dragPreview.name }}</b>
      <span>{{ dragPreview.start }} → {{ dragPreview.end }}</span>
      <em v-if="dragPreview.days !== 0">
        {{ dragPreview.days > 0 ? '后移' : '提前' }} {{ Math.abs(dragPreview.days) }} 天
        <template v-if="dragPreview.mode !== 'move'">（{{ dragPreview.mode === 'start' ? '改开始' : '改完成' }}）</template>
      </em>
      <em v-else class="drag-tip-idle">左右拖动改期</em>
    </div>

    <!-- 拖动中同步在行首显示一份：指针跟随的标签在屏幕边缘可能被挤出去 -->
    <p v-if="dragPreview" class="drag-live" role="status">
      正在调整「{{ dragPreview.name }}」：
      <b>{{ dragPreview.start }} → {{ dragPreview.end }}</b>
      <span v-if="dragPreview.days !== 0">
        （{{ dragPreview.days > 0 ? '后移' : '提前' }} {{ Math.abs(dragPreview.days) }} 天）
      </span>
    </p>
  </section>
</template>

<style scoped>
/* 跟着指针的实时日期标签 */
.drag-tip {
  position: fixed;
  z-index: 60;
  display: grid;
  gap: 1px;
  padding: 6px 10px;
  border: 1px solid var(--ink);
  background: var(--ink);
  color: white;
  font-size: 12px;
  line-height: 1.5;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 14px rgba(16, 39, 56, .28);
}

.drag-tip b { font-size: 12px; font-weight: 700; }
.drag-tip span { color: var(--cyan); font-weight: 700; }
.drag-tip em { color: rgba(255, 255, 255, .75); font-size: 12px; font-style: normal; }
.drag-tip-idle { color: rgba(255, 255, 255, .5); }

/* 指针标签在屏幕边缘可能被挤出去，这里再固定给一份 */
.drag-live {
  position: sticky;
  bottom: 0;
  z-index: 5;
  margin: 0;
  padding: 7px 12px;
  border-top: 1px solid var(--line);
  background: #EEF4FA;
  color: var(--ink);
  font-size: 12px;
}

.drag-live b { color: var(--blue); }

.gantt-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 8px;
}

.zoom-control,
.task-scope-control {
  display: flex;
  align-items: center;
  gap: 4px;
}

.zoom-control > span,
.task-scope-control > span {
  margin-right: 4px;
  color: var(--muted);
  font-size: 12px;
}

.zoom-control button,
.task-scope-control button {
  min-height: 42px;
  padding: 0 12px;
  border: 1px solid var(--line);
  color: var(--muted);
  background: var(--white);
  cursor: pointer;
  font-size: 13px;
}

.zoom-control button.active,
.task-scope-control button.active {
  color: white;
  background: var(--blue);
  border-color: var(--blue);
}

.task-scope-control button b {
  margin-left: 4px;
  font: inherit;
}

.gantt-scroll {
  width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  cursor: grab;
  outline: none;
  touch-action: pan-y;
}

.gantt-scroll:focus-visible {
  box-shadow: 0 0 0 2px var(--blue);
}

.gantt-scroll.dragging {
  cursor: grabbing;
  user-select: none;
}

.gantt-board {
  min-width: 100%;
  overflow: visible;
  position: relative;
}

/* ---------- 前后置依赖 ---------- */

.dep-control { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.dep-control > span { color: var(--muted); font-size: 12px; }
.dep-control button {
  padding: 4px 11px;
  border: 1px solid var(--line);
  background: var(--white);
  color: var(--ink);
  font: 700 12px var(--body);
  cursor: pointer;
}
.dep-control button.active { border-color: var(--blue); background: #EEF4FA; color: var(--blue); }
.dep-hidden { color: var(--warning); font-size: 12px; }

.dep-linking-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  padding: 8px 12px;
  border-left: 3px solid var(--blue);
  background: #EEF4FA;
  color: var(--ink);
  font-size: 12px;
}
.dep-linking-bar button {
  border: 0;
  background: transparent;
  color: var(--blue);
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.dep-layer {
  position: absolute;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}

.dep-link .dep-line { fill: none; stroke: #6B86A3; stroke-width: 1.5; }
.dep-link .dep-arrow { fill: #6B86A3; }
.dep-link.violated .dep-line { stroke: var(--risk); stroke-width: 2; stroke-dasharray: 5 3; }
.dep-link.violated .dep-arrow { fill: var(--risk); }
/* 1.5px 的线点不中；加一条透明粗线专门接鼠标 */
.dep-link .dep-hit { fill: none; stroke: transparent; stroke-width: 12; }
.dep-link.clickable { pointer-events: auto; cursor: pointer; }
.dep-link.clickable:hover .dep-line { stroke: var(--blue); stroke-width: 2.5; }
.dep-link.clickable:hover .dep-arrow { fill: var(--blue); }

.dep-badge {
  padding: 0 4px;
  border: 1px solid #B9C8D8;
  color: #3C5C79;
  font-size: 10px;
  font-style: normal;
}
.dep-badge.risk { border-color: var(--risk); color: var(--risk); font-weight: 700; }

.dep-start {
  position: absolute;
  left: 352px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #B9C8D8;
  background: var(--white);
  color: #3C5C79;
  font-size: 12px;
  line-height: 1;
  opacity: 0;
  cursor: pointer;
}
.gantt-row:hover .dep-start, .dep-start:focus-visible { opacity: 1; }
.dep-start:hover { border-color: var(--blue); color: var(--blue); }

.gantt-board.linking .gantt-tree-cell:hover { outline: 2px solid var(--blue); outline-offset: -2px; }
.plan-bar.link-source { outline: 2px solid var(--blue); outline-offset: 2px; }
.plan-bar.dep-violated { box-shadow: inset 0 0 0 2px var(--risk); }

.gantt-head,
.gantt-row {
  min-width: 0;
  grid-template-columns: 380px minmax(0, 1fr);
}

/* 连线按钮要贴在树列右边缘，需要行本身是定位上下文 */
.gantt-row { position: relative; }

.gantt-tree-head,
.gantt-tree-cell {
  position: sticky;
  left: 0;
}

.gantt-tree-head {
  z-index: 6;
  background: var(--paper-2);
}

.gantt-tree-cell {
  z-index: 3;
  background: var(--paper);
}

/*
 * 行分成两个并列按钮：左边点名字（展开子节点），右边点「改期 / 状态」（开抽屉）。
 * 外层保持 sticky 容器——嵌套 button 是非法 HTML，不能把操作按钮塞进名字按钮里。
 *
 * 全局 styles.css 里的 `.gantt-tree-cell > div` 管着名字块的省略号和 min-width:0，
 * 拆成两个按钮后名字块不再是它的直接子元素，那几条就失效了——
 * 结果是名字被挤成一列一个字（试用反馈 0818）。这里按新结构重新写一遍，
 * 并且全用 flex：栏位数会随「有没有展开箭头」变化，固定列数的 grid 必然错位。
 */
.gantt-tree-cell {
  display: flex;
  align-items: center;
  gap: 9px;
  padding-right: 11px;
  border-right: 1px solid var(--line);
}

.tree-name {
  display: flex;
  align-items: center;
  gap: 9px;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

/* 名字块吃掉剩余宽度，长名字用省略号截断而不是换行 */
.tree-name > div {
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 3px;
}
.tree-name > div b {
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tree-name > div small {
  overflow: hidden;
  color: var(--muted);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tree-name:hover,
.tree-name:focus-visible { background: #EEF4FA; }

/* 箭头列宽度固定：没有子节点的行也占同样的位，行首才对得齐 */
.tree-caret { flex: 0 0 11px; color: var(--muted); font-size: 11px; }
.tree-name > .wbs-code { flex: 0 0 auto; }
.tree-name > .node-markers { flex: 0 0 auto; }

button.gantt-tree-cell.editing,
.gantt-tree-cell.editing {
  background: #E3EDF7;
  box-shadow: inset 0 0 0 1px var(--blue);
}

/* 平时低调，悬停/展开时才亮起来——229 行不能行行都在喊 */
.row-action {
  flex: 0 0 auto;
  padding: 3px 7px;
  border: 1px solid var(--line);
  color: var(--muted);
  background: var(--white);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.gantt-tree-cell:hover .row-action,
button.gantt-tree-cell:focus-visible .row-action,
button.gantt-tree-cell.editing .row-action {
  border-color: var(--blue);
  color: var(--blue);
}

.row-action.missing {
  border-color: #E6B75C;
  color: #7A4E00;
  background: #FFF3D6;
}

.gantt-row.unscheduled .gantt-tree-cell {
  background: #FFF9ED;
}

.gantt-row.unscheduled button.gantt-tree-cell:hover,
.gantt-row.unscheduled button.gantt-tree-cell:focus-visible {
  background: #FDF0D6;
}

/* 已排期的行编辑时，表单要盖住甘特条才看得清 */
.gantt-row .inline-schedule {
  z-index: 4;
}

.status-field select {
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--line);
  background: var(--white);
  font: inherit;
  font-size: 12px;
}

.gantt-row.focused {
  box-shadow: inset 3px 0 0 var(--blue);
}

.missing-badge {
  flex: 0 0 auto;
  padding: 3px 6px;
  border: 1px solid #E6B75C;
  color: #7A4E00;
  background: #FFF3D6;
  font-size: 10px;
  font-weight: 800;
}

.missing-action {
  min-height: 28px;
  cursor: pointer;
}

.missing-action:disabled {
  cursor: not-allowed;
  opacity: .65;
}

.inline-schedule {
  position: absolute;
  z-index: 2;
  inset: 7px 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 0 10px;
  border: 1px dashed #D39A33;
  background: rgba(255, 249, 237, .94);
}

/* 编辑中是中性态，不是「缺日期」告警态——琥珀色只该表示数据有缺口 */
.inline-schedule.editing {
  border: 1px solid var(--blue);
  background: rgba(255, 255, 255, .97);
}

.timeline-head .no-range-head {
  left: 16px !important;
  color: var(--muted);
}

.inline-schedule > span {
  display: grid;
  gap: 1px;
  color: #7A4E00;
  font-size: 12px;
}

.inline-schedule > span small {
  color: var(--muted);
  font-size: 10px;
}

.inline-schedule-form {
  width: 100%;
  display: flex;
  align-items: end;
  gap: 7px;
}

.inline-schedule-form label {
  display: grid;
  gap: 2px;
  color: var(--muted);
  font-size: 10px;
}

.inline-schedule-form input {
  min-height: 34px;
  padding: 0 7px;
  border: 1px solid #C7A15B;
  background: white;
}

.inline-schedule-form .button,
.inline-schedule-form .inline-button {
  min-height: 34px;
}

.date-arrow {
  padding-bottom: 9px;
  color: #9B6A13;
}

/* 甘特行里的历史面板：限宽，别把内联表单撑变形 */
.gantt-history {
  flex-basis: 100%;
  max-width: 640px;
}

.date-error {
  color: var(--risk);
  font-size: 12px;
}

.gantt-legend .missing {
  border: 1px dashed #D39A33;
  background: #FFF3D6;
}

/*
  条子 = 浅底 + 深色填充（0930）。全局 styles.css 里 .plan-bar 是一整条实心蓝，
  这里只覆盖底色和字色，定位、高度、拖拽抓手都沿用原样。
  前面带 .gantt-board 是为了压过全局 .plan-bar.overdue——同样两个类名时谁赢看 CSS 加载顺序，靠不住。
  字色跟着底走：填充没盖到字的位置（< 80%）用深字，盖到了用白字。
*/
.gantt-board .plan-bar {
  color: var(--ink);
  background: #D3E2F5;
  box-shadow: inset 0 0 0 1px rgba(0, 117, 174, .45);
}
.gantt-board .plan-bar.overdue {
  background: #F4DAD8;
  box-shadow: inset 0 0 0 1px rgba(181, 56, 49, .55);
}
.gantt-board .plan-bar.completed {
  color: white;
  background: var(--green);
  box-shadow: inset 0 -3px rgba(0, 0, 0, .12);
}
.gantt-board .plan-bar.label-on-fill { color: white; }
.gantt-board .plan-bar > span { position: relative; z-index: 1; }
.bar-fill {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background: var(--blue);
  box-shadow: inset 0 -3px rgba(0, 0, 0, .12);
  pointer-events: none;
}
.plan-bar.overdue .bar-fill { background: var(--risk); }
.plan-bar.completed .bar-fill { background: var(--green); }
/* 拖动抓手压在填充上面，否则深色部分那头抓不住 */
.plan-bar .bar-handle { z-index: 2; }
.plan-bar:hover .bar-handle { background: rgba(0, 117, 174, .25); }
.gantt-legend i.plan { background: #D3E2F5; box-shadow: inset 0 0 0 1px rgba(0, 117, 174, .45); height: 8px; }
.gantt-legend i.progress { background: var(--blue); height: 8px; }
.gantt-legend i.done { background: var(--green); height: 8px; }

.gantt-empty-row {
  min-height: 54px;
  display: grid;
  place-items: center;
  color: var(--muted);
  border-top: 1px solid var(--line);
  font-size: 13px;
}

@media (max-width: 600px) {
  .gantt-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .zoom-control,
  .task-scope-control {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
  }

  .zoom-control > span,
  .task-scope-control > span {
    grid-column: 1 / -1;
  }

  .gantt-head,
  .gantt-row {
    grid-template-columns: 280px minmax(0, 1fr);
  }

  .dep-start { left: 252px; }

  .inline-schedule-form {
    align-items: stretch;
    flex-wrap: wrap;
  }
}
</style>
